import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { cities, sampleFreeContent, samplePaidPreview } from '../data/mock';
import { MapPin, Clock, Check, Lock, Utensils, Camera, Compass, ChevronRight } from 'lucide-react';

const CityPage = () => {
  const { slug } = useParams();
  const city = cities.find((c) => c.slug === slug);
  const [activeTab, setActiveTab] = useState('overview');

  if (!city) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="pt-32 pb-16 text-center">
          <h1 className="text-4xl font-serif text-stone-900">City not found</h1>
          <Link to="/destinations" className="text-amber-700 mt-4 inline-block">
            Back to Destinations
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Use Rome's sample content as fallback
  const freeContent = sampleFreeContent[slug] || sampleFreeContent.rome;
  const paidPreview = samplePaidPreview[slug] || samplePaidPreview.rome;

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[450px]">
        <div className="absolute inset-0">
          <img
            src={city.image}
            alt={city.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        </div>
        <div className="relative h-full flex flex-col justify-end max-w-7xl mx-auto px-6 lg:px-8 pb-12">
          <p className="text-amber-200 text-lg font-medium mb-2">{city.tagline}</p>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif text-white mb-4">
            {city.name}
          </h1>
          <p className="text-white/80 text-lg max-w-2xl">{city.description}</p>
        </div>
      </section>

      {/* Sticky Nav */}
      <div className="sticky top-16 z-30 bg-white border-b border-stone-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex gap-8 overflow-x-auto">
            {['overview', 'where-to-eat', 'what-to-see', 'get-the-guide'].map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  const element = document.getElementById(tab);
                  if (element) {
                    const offset = 150;
                    const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
                    window.scrollTo({ top, behavior: 'smooth' });
                  }
                }}
                className={`py-4 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab
                    ? 'border-amber-700 text-amber-700'
                    : 'border-transparent text-stone-500 hover:text-stone-900'
                }`}
              >
                {tab === 'overview' && 'Overview'}
                {tab === 'where-to-eat' && 'Where to Eat'}
                {tab === 'what-to-see' && 'What to See'}
                {tab === 'get-the-guide' && 'Get the Guide'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Free: Overview Section */}
            <section id="overview" className="mb-16">
              <div className="flex items-center gap-2 mb-6">
                <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                  Free
                </span>
                <h2 className="text-2xl font-serif text-stone-900">Overview</h2>
              </div>
              
              <p className="text-stone-600 text-lg leading-relaxed mb-8">
                {freeContent.overview}
              </p>

              <div className="bg-stone-100 rounded-xl p-6 mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-5 h-5 text-amber-700" />
                  <h3 className="font-medium text-stone-900">Best Time to Visit</h3>
                </div>
                <p className="text-stone-600">{freeContent.bestTime}</p>
              </div>

              {/* Neighborhoods */}
              <h3 className="text-xl font-serif text-stone-900 mb-4">Neighborhoods to Know</h3>
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {freeContent.neighborhoods.map((hood) => (
                  <div key={hood.name} className="bg-white rounded-xl p-5 border border-stone-200">
                    <h4 className="font-medium text-stone-900 mb-1">{hood.name}</h4>
                    <p className="text-stone-500 text-sm">{hood.vibe}</p>
                  </div>
                ))}
              </div>

              {/* Must See */}
              <h3 className="text-xl font-serif text-stone-900 mb-4">The Essentials</h3>
              <ul className="space-y-3">
                {freeContent.mustSee.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span className="text-stone-600">{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Locked: Where to Eat Preview */}
            <section id="where-to-eat" className="mb-16">
              <div className="flex items-center gap-2 mb-6">
                <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Full Guide
                </span>
                <h2 className="text-2xl font-serif text-stone-900">Where to Eat</h2>
              </div>
              
              <p className="text-stone-600 mb-6">
                Beyond the tourist menus: trattorias, wine bars, and street food spots where locals actually eat.
              </p>

              {/* Preview Cards */}
              <div className="space-y-4 mb-6">
                {paidPreview.restaurants.map((restaurant, i) => (
                  <div 
                    key={i} 
                    className={`bg-white rounded-xl p-5 border border-stone-200 ${
                      restaurant.name === '...' ? 'opacity-60' : ''
                    }`}
                  >
                    {restaurant.name !== '...' ? (
                      <>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-stone-900">{restaurant.name}</h4>
                          <span className="text-amber-700 text-sm">{restaurant.type}</span>
                        </div>
                        <p className="text-stone-600 text-sm">{restaurant.why}</p>
                      </>
                    ) : (
                      <div className="flex items-center gap-2 text-stone-500">
                        <Lock className="w-4 h-4" />
                        <span>{restaurant.type}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Unlock CTA */}
              <div className="bg-stone-900 rounded-xl p-6 text-center">
                <h4 className="text-white font-medium mb-2">Get all 10 restaurant recommendations</h4>
                <p className="text-white/70 text-sm mb-4">Plus insider tips on what to order and when to go</p>
                <a
                  href={`/checkout.php?product=${city.slug}-guide`}
                  className="inline-block px-6 py-3 bg-white text-stone-900 rounded-full font-medium hover:bg-amber-50 transition-colors"
                >
                  Unlock for {city.guidePrice}
                </a>
              </div>
            </section>

            {/* Locked: Hidden Gems Preview */}
            <section id="what-to-see" className="mb-16">
              <div className="flex items-center gap-2 mb-6">
                <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Full Guide
                </span>
                <h2 className="text-2xl font-serif text-stone-900">Off the Beaten Path</h2>
              </div>
              
              <p className="text-stone-600 mb-6">
                The spots that don't make it into guidebooks. Quiet corners, local secrets, unexpected discoveries.
              </p>

              <div className="space-y-4">
                {paidPreview.hiddenGems.map((gem, i) => (
                  <div 
                    key={i} 
                    className={`bg-white rounded-xl p-5 border border-stone-200 ${
                      gem.name === '...' ? 'opacity-60' : ''
                    }`}
                  >
                    {gem.name !== '...' ? (
                      <>
                        <h4 className="font-medium text-stone-900 mb-1">{gem.name}</h4>
                        <p className="text-stone-600 text-sm">{gem.what}</p>
                      </>
                    ) : (
                      <div className="flex items-center gap-2 text-stone-500">
                        <Lock className="w-4 h-4" />
                        <span>{gem.what}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Sidebar - Pricing */}
          <div className="lg:col-span-1">
            <div className="sticky top-32 space-y-6">
              {/* Full Guide */}
              <div id="get-the-guide" className="bg-white rounded-2xl p-6 shadow-lg border border-stone-200">
                <div className="text-center mb-6">
                  <p className="text-sm text-stone-500 mb-1">Full City Guide</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-serif text-stone-900">{city.guidePrice}</span>
                    <span className="text-stone-500">one-time</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  {[
                    '10+ restaurant recommendations',
                    'Hidden gems & local secrets',
                    'Off-the-beaten-path spots',
                    'Neighborhood deep-dives',
                    'Day trip suggestions',
                    'Practical tips & timing'
                  ].map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <span className="text-stone-600 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <a 
                  href={`/checkout.php?product=${city.slug}-guide`}
                  className="block w-full py-3 bg-stone-900 text-white text-center rounded-full font-medium hover:bg-stone-800 transition-colors mb-3"
                >
                  Get the Full Guide
                </a>
                <p className="text-center text-stone-500 text-xs">7-day money-back guarantee</p>
              </div>

              {/* Custom Itinerary */}
              <div className="bg-stone-50 rounded-2xl p-6 border border-stone-200">
                <div className="text-center mb-4">
                  <p className="text-sm text-stone-500 mb-1">Custom Itinerary</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-3xl font-serif text-stone-900">{city.itineraryPrice}</span>
                    <span className="text-stone-500">one-time</span>
                  </div>
                </div>
                <p className="text-stone-600 text-sm text-center mb-4">
                  Everything in the guide, plus a personalized day-by-day plan built for your dates and interests.
                </p>
                <button className="w-full py-3 border border-stone-300 text-stone-700 rounded-full font-medium hover:bg-stone-100 transition-colors">
                  Get Started
                </button>
              </div>

              {/* Other Destinations */}
              <div className="bg-white rounded-xl p-5 border border-stone-200">
                <h4 className="font-medium text-stone-900 mb-4">Other Destinations</h4>
                <div className="space-y-2">
                  {cities.filter(c => c.slug !== slug).slice(0, 4).map((otherCity) => (
                    <Link
                      key={otherCity.slug}
                      to={`/city/${otherCity.slug}`}
                      className="flex items-center justify-between py-2 text-stone-600 hover:text-amber-700 transition-colors"
                    >
                      <span>{otherCity.name}</span>
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CityPage;
